// app/components/thailand-premium-tapioca-starch-products/FoodGrade.tsx
"use client";

import * as React from "react";
import { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

export default function FoodGrade() {
  const t = useTranslations("OurProducts");
  const locale = useLocale();

  const [pagePath, setPagePath] = useState<string>("/thailand-premium-tapioca-starch-products");
  useEffect(() => {
    if (typeof window === "undefined") return;
    setPagePath(window.location.pathname);
  }, []);

  // ---- default text/image จาก i18n + static asset (ดีต่อ SEO + SSR) ----
  const defaultTitle1 = t("FoodGrade.title1");
  const defaultList1Item1 = t("FoodGrade.List1.item1");
  const defaultList1Item2 = t("FoodGrade.List1.item2");
  const defaultList1Item3 = t("FoodGrade.List1.item3");

  const defaultTitle2 = t("FoodGrade.title2");
  const defaultList2Item1 = t("FoodGrade.List2.item1");
  const defaultList2Item2 = t("FoodGrade.List2.item2");
  const defaultList2Item3 = t("FoodGrade.List2.item3");
  const defaultList2Item4 = t("FoodGrade.List2.item4");
  const defaultList2Item5 = t("FoodGrade.List2.item5");

  const defaultImage = "/assets/thailand-premium-tapioca-starch-products/foodgrade/food.png";

  // ---- state สำหรับแสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title1, setTitle1] = useState(defaultTitle1);
  const [list1Item1, setList1Item1] = useState(defaultList1Item1);
  const [list1Item2, setList1Item2] = useState(defaultList1Item2);
  const [list1Item3, setList1Item3] = useState(defaultList1Item3);

  const [title2, setTitle2] = useState(defaultTitle2);
  const [list2Item1, setList2Item1] = useState(defaultList2Item1);
  const [list2Item2, setList2Item2] = useState(defaultList2Item2);
  const [list2Item3, setList2Item3] = useState(defaultList2Item3);
  const [list2Item4, setList2Item4] = useState(defaultList2Item4);
  const [list2Item5, setList2Item5] = useState(defaultList2Item5);

  const [foodImageSrc, setFoodImageSrc] = useState(defaultImage);

  // ---- locale เปลี่ยน → reset กลับ default แล้วค่อยให้ inline override ทับทีหลัง ----
  useEffect(() => {
    setTitle1(defaultTitle1);
    setList1Item1(defaultList1Item1);
    setList1Item2(defaultList1Item2);
    setList1Item3(defaultList1Item3);

    setTitle2(defaultTitle2);
    setList2Item1(defaultList2Item1);
    setList2Item2(defaultList2Item2);
    setList2Item3(defaultList2Item3);
    setList2Item4(defaultList2Item4);
    setList2Item5(defaultList2Item5);

    setFoodImageSrc(defaultImage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  useEffect(() => {
    if (!pagePath) return;

    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          // fallback เผื่อในอนาคต selector มี data-cms-key
          if (!key && e.selector) {
            const match = e.selector.match(/data-cms-key="([^"]+)"/);
            if (match) key = match[1];
          }

          if (key) {
            map[key] = e.value;
          }
        });

        // ---- map key → state (text) ----
        if (map["ourproducts.foodgrade.title1"])
          setTitle1(map["ourproducts.foodgrade.title1"]);

        if (map["ourproducts.foodgrade.list1.item1"])
          setList1Item1(map["ourproducts.foodgrade.list1.item1"]);
        if (map["ourproducts.foodgrade.list1.item2"])
          setList1Item2(map["ourproducts.foodgrade.list1.item2"]);
        if (map["ourproducts.foodgrade.list1.item3"])
          setList1Item3(map["ourproducts.foodgrade.list1.item3"]);

        if (map["ourproducts.foodgrade.title2"])
          setTitle2(map["ourproducts.foodgrade.title2"]);

        if (map["ourproducts.foodgrade.list2.item1"])
          setList2Item1(map["ourproducts.foodgrade.list2.item1"]);
        if (map["ourproducts.foodgrade.list2.item2"])
          setList2Item2(map["ourproducts.foodgrade.list2.item2"]);
        if (map["ourproducts.foodgrade.list2.item3"])
          setList2Item3(map["ourproducts.foodgrade.list2.item3"]);
        if (map["ourproducts.foodgrade.list2.item4"])
          setList2Item4(map["ourproducts.foodgrade.list2.item4"]);
        if (map["ourproducts.foodgrade.list2.item5"])
          setList2Item5(map["ourproducts.foodgrade.list2.item5"]);

        // ---- image ----
        if (map["ourproducts.foodgrade.image"])
          setFoodImageSrc(map["ourproducts.foodgrade.image"]);
      } catch (err) {
        console.warn("[FoodGrade] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box
      id="food-grade"
      className="foodgrade-wrap"
      sx={{
        pb: { xs: 0, md: 5 },
        pt: { xs: 5, md: 0 }
      }}>
      <Container className="container" sx={{ px: { xs: 0, md: 3 } }}>
        <Box className="foodgrade-content" sx={{
          p: { xs: 3, md: 5 }
        }}>
          <Grid container spacing={3}>
            {/* desktop image */}
            <Grid
              size={{ xs: 12, md: 5 }}
              data-aos="fade-right"
              sx={{
                position: "relative"
              }}
            >
              <Box
                className="foodgrade-img"
                sx={{
                  display: { xs: "none", sm: "block" }
                }}
              >
                <Image
                  src={foodImageSrc}
                  title="thailand food grade tapioca"
                  alt="Food grade non-GMO tapioca starch applications — bubble tea pearls, noodles and dim sum"
                  width={826}
                  height={692}
                  className="img-fluid"
                  data-cms-key="ourproducts.foodgrade.image"
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Box
                className="foodgrade-list"
                data-aos="fade-left"
                sx={{
                  p: 5,
                  mb: 4
                }}>
                <Typography
                  variant="h2"
                  sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                  data-cms-key="ourproducts.foodgrade.title1"
                >
                  {title1}
                </Typography>
                <ul>
                  <li data-cms-key="ourproducts.foodgrade.list1.item1">
                    {list1Item1}
                  </li>
                  <li data-cms-key="ourproducts.foodgrade.list1.item2">
                    {list1Item2}
                  </li>
                  <li data-cms-key="ourproducts.foodgrade.list1.item3">
                    {list1Item3}
                  </li>
                </ul>
              </Box>

              <Box className="foodgrade-list" data-aos="fade-left" sx={{
                p: 5
              }}>
                <Typography
                  variant="h2"
                  sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                  data-cms-key="ourproducts.foodgrade.title2"
                >
                  {title2}
                </Typography>
                <ul>
                  <li data-cms-key="ourproducts.foodgrade.list2.item1">
                    {list2Item1}
                  </li>
                  <li data-cms-key="ourproducts.foodgrade.list2.item2">
                    {list2Item2}
                  </li>
                  <li data-cms-key="ourproducts.foodgrade.list2.item3">
                    {list2Item3}
                  </li>
                  <li data-cms-key="ourproducts.foodgrade.list2.item4">
                    {list2Item4}
                  </li>
                  <li data-cms-key="ourproducts.foodgrade.list2.item5">
                    {list2Item5}
                  </li>
                </ul>
              </Box>

              {/* mobile image */}
              <Box
                className="foodgrade-img"
                data-aos="fade-up"
                sx={{
                  display: { xs: "block", sm: "none" },
                  mt: 3
                }}>
                <Image
                  src={foodImageSrc}
                  title="food grade tapioca"
                  alt="food grade tapioca"
                  width={826}
                  height={692}
                  className="img-fluid"
                  data-cms-key="ourproducts.foodgrade.image"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}